<template>
  <b-modal
    v-if="currentPoste"
    id="modal-edit-poste-livraison"
    :title="isUpdateFrom ? 'Éditer le poste de livraison' : 'Ajouter un poste de livraison'"
    no-close-on-backdrop
    no-close-on-esc
    size="xl"
    title-class="modal__title modal__title--edit"
    @cancel="$emit('reload-parc')"
    @ok="isUpdateFrom ? putPosteLivraison() : postPosteLivraison()"
  >
    <b-form class="poste-livraison-form pt-2 px-md-3">
      <!-- Situation -->
      <b-form-group
        class="mb-0"
        label="Localisation"
        label-class="fw-bold pt-0"
        label-cols-lg="3"
        label-size="lg"
      >
        <b-row>
          <b-col md="6">
            <b-form-group
              id="input-group-xposteLivraison"
              :label="`Coordonnée X (${parc.projection.libelle})*`"
              class="mb-0"
              label-for="input-xposteLivraison"
            >
              <b-form-input
                id="input-xposteLivraison"
                v-model="currentPoste.xposteLivraison"
                :no-wheel="true"
                placeholder="Indiquer les coordonnées x"
                required
                step=".01"
                type="number"
                @input="$emit('on-input-coordinates', currentPoste)"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              id="input-group-yposteLivraison"
              :label="`Coordonnée Y (${parc.projection.libelle})*`"
              class="mb-0"
              label-for="input-yposteLivraison"
            >
              <b-form-input
                id="input-yposteLivraison"
                v-model="currentPoste.yposteLivraison"
                :no-wheel="true"
                placeholder="Indiquer les coordonnées y"
                required
                step=".01"
                type="number"
                @input="$emit('on-input-coordinates', currentPoste)"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="small text-justify mt-1 mb-3">
            Veuillez indiquer les coordonnées du poste de livraison, au format
            <span>{{ parc.projection.libelle }}</span>. Assurez-vous d'avoir saisi le(s) commune(s) d'installation
            dans le formulaire du parc.
          </b-col>
        </b-row>
      </b-form-group>

      <!-- Alertes Map -->
      <div slot="image" class="edit-poste-livraison-map">
        <div class="edit-poste-livraison-map__alert">
          <div v-if="isLoadingCoordinatesValidation != null">
            <b-alert
              v-if="isLoadingCoordinatesValidation"
              :model-value="true"
              class="rounded"
              show
              variant="info"
            >
              <b-spinner label="Small Spinner" small type="grow"></b-spinner>
              <span class="ms-2">
                  Vérification des coordonnées saisies...
                </span>
            </b-alert>
            <b-alert
              v-else
              :model-value="true"
              :variant="coordinatesAlert.state"
              class="rounded"
              show
            >
              {{ coordinatesAlert.text }}
            </b-alert>
          </div>
        </div>
        <l-map :center="currentMapCenter" :zoom="currentMapZoom" @click="$emit('on-click-map', $event, currentPoste)">
          <l-tile-layer
            :attribution="baseLayer.attribution"
            :url="baseLayer.url"
          ></l-tile-layer>
          <l-marker
            v-for="(marker, index) in markers"
            :key="index"
            :lat-lng="marker"
            draggable
            @click="$emit('remove-marker', index, currentPoste)"
            @drag="$emit('update-coordinates', $event, currentPoste)"
            @dragend="$emit('check-coordinates', currentPoste)"
            @dragstart="$emit('set-loading-validation')"
          >
          </l-marker>
          <l-polygon
            v-for="(communeGeom, index) in communeGeoms"
            :key="'polygon' + index"
            :color="color"
            :lat-lngs="communeGeom"
            fillColor="#3388ff"
          ></l-polygon>
        </l-map>
      </div>
    </b-form>
    <template #footer="{ ok, cancel }">
      <!-- Emulate built in modal footer ok and cancel button actions -->
      <span
        class="d-inline-block"
        tabindex="0"
        id="poste-edit-btn"
      >
          <b-tooltip target="#poste-edit-btn">
            {{ disableSubmitButton ? "La localisation du poste de livraison n'est pas valide" : '' }}
          </b-tooltip>
          <b-button
            :disabled="disableSubmitButton"
            :title="
              isUpdateFrom
                ? 'Enregistrer les modifications'
                : 'Enregistrer le nouveau poste de livraison'
            "
            variant="info"
            @click="ok()"
          >
            Valider
          </b-button>
        </span>
      <b-button
        title="Annuler les modifications"
        variant="default"
        @click="cancel()"
      >
        Annuler
      </b-button>
    </template>
  </b-modal>
</template>
<script setup>
import {LMap, LMarker, LPolygon, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {
  BAlert,
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
  BRow,
  BSpinner,
  BTooltip
} from "bootstrap-vue-next";

import {useReactiveStoreGetters} from "@/composables/useStore";
import {computed, onMounted, ref, watch} from "vue";
import proj4 from "proj4";
import {mapService} from "@/service/map.service";
import {posteLivraisonService} from "@/service/posteLivraison.service";
import {collectiviteService} from "@/service/collectivite.service";

// Lifecycle hook
onMounted(() => {
  init()
})

// Émissions
defineEmits([
  'reload-parc',
  'remove-marker',
  'update-coordinates',
  'on-input-coordinates',
  'on-click-map',
  'check-coordinates',
  'set-loading-validation'
])

// Props
const {
  poste,
  mapZoom,
  mapCenter,
  markers,
  addNotification,
  coordinatesAlert
} = defineProps({
  mapZoom: {
    type: Number,
    required: true,
  },
  mapCenter: {
    required: true,
  },
  poste: {
    type: [Object, null],
    required: true,
  },
  markers: {
    type: Array,
    required: true,
  },
  addNotification: {
    type: Function,
    required: true,
  },
  isUpdateFrom: {
    type: Boolean,
    required: true,
  },
  isLoadingCoordinatesValidation: {
    type: [Boolean, null],
    required: true,
  },
  coordinatesAlert: {
    type: Object,
    required: true,
  },
})

// Getters
const {
  parc,
  isInventaireCommunes,
  baseLayer,
  epsg,
} = useReactiveStoreGetters([
  "parc",
  "isInventaireCommunes",
  "baseLayer",
  "epsg",
]);


// Data
const currentPoste = ref(poste)
const communeGeoms = ref([])
const color = "#FFFFCC";

// Lifecycle hook
onMounted(() => {
  init()
})

const init = () => {
  addPolygonCommunesParc();
};

// Watch for changes in the prop
watch(() => poste, (newValue) => {
  currentPoste.value = {...newValue};
})

// Computed
const wgs64FromXY = computed(() => {
  if (currentPoste.value &&
    currentPoste.value.xposteLivraison !== null &&
    currentPoste.value.xposteLivraison !== undefined &&
    currentPoste.value.yposteLivraison !== null &&
    currentPoste.value.yposteLivraison !== undefined
  ) {
    return proj4(`EPSG:${epsg.value}`).inverse([
      parseFloat(currentPoste.value.xposteLivraison),
      parseFloat(currentPoste.value.yposteLivraison)
    ])
  }
  return null
});


const currentMapCenter = computed(() => {
  if (markers && markers.length > 0) {
    return [markers[0][0], markers[0][1]];
  } else if (wgs64FromXY.value) {
    return [wgs64FromXY.value[1], wgs64FromXY.value[0]];
  } else if (communeGeoms.value && communeGeoms.value[0]) {
    return mapService.getMultipolygonCenter(communeGeoms.value[0])
  }
  return mapCenter;
});

const currentMapZoom = computed(() => {
  if ((currentPoste.value &&
      currentPoste.value.xposteLivraison &&
      currentPoste.value.yposteLivraison) ||
    (communeGeoms.value && communeGeoms.value.length > 0)) {
    return 13;
  }
  return mapZoom;
});

// Methods

const setGeomWgs84FromXY = () => {
  if (wgs64FromXY.value) {
    currentPoste.value.geomWgs84 = {
      type: "Point",
      coordinates: [wgs64FromXY.value[0], wgs64FromXY.value[1]]
    };
  }
};

const postPosteLivraison = () => {
  setGeomWgs84FromXY();

  posteLivraisonService
    .postPosteLivraison(currentPoste.value)
    .then(response => {
      const httpMethod = "POST";
      addNotification(response, httpMethod);
    })
    .catch(err => {
      console.log(err);
    });
};

const putPosteLivraison = () => {
  setGeomWgs84FromXY();

  posteLivraisonService
    .putPosteLivraison(currentPoste.value)
    .then(response => {
      const httpMethod = "PUT";
      addNotification(response, httpMethod);
    })
    .catch(err => {
      console.log(err);
    });
};

const addPolygonCommunesParc = () => {
  if (parc.value && isInventaireCommunes.value) {
    communeGeoms.value = [];
    //Récupération des communes du parc
    collectiviteService
      .getCommuneGeomByParc(parc.value)
      .then(data => {
        if (data) {
          const polygons = [];
          //Transformation des coordonées en tableau de long, lat
          data.forEach(communeJson => {
            communeJson.geomWgs84.coordinates.forEach(communeFragment => {
              const polygon = communeFragment[0].map(
                point => {
                  return [point[1], point[0]];
                }
              );
              polygons.push(polygon);
            })
          });
          communeGeoms.value = polygons;
        }
      })
      .catch(err => {
        console.log(err);
      });
  }
}

const disableSubmitButton = computed(() =>
  coordinatesAlert.state !== "success"
);

</script>
<style lang="scss">
.edit-poste-livraison-map {
  height: 400px;
  position: relative;

  &__alert {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    margin-left: 55px;
  }
}
</style>
